import { inject, Injectable, signal } from '@angular/core';
import { ChatService } from '../services/chat.service';
import { EnvironmentService } from '@big-direkt/utils/environment';
import { firstValueFrom } from 'rxjs';
import { type Message } from '../interfaces/message';
import { type MessageButton } from '../interfaces/message-button';
import { type ItscMessageResponse } from '../interfaces/itsc-message-response';
import { ApiService } from './api.service';

@Injectable({
    providedIn: 'root',
})
export class MessagesService {
    private readonly apiService = inject(ApiService);
    private readonly chatService = inject(ChatService);
    private readonly environmentService = inject(EnvironmentService);

    public readonly config = this.chatService.config;
    public readonly messages = signal<Message[]>([]);
    public readonly updated = signal<Message | undefined>(undefined);

    public add(message: Message): void {
        this.messages.update(messages => {
            if (this.config().bot.targetBlank) {
                message.text = this.addTargetBlank(message.text);
            }

            messages.push(message);

            return messages;
        });

        this.updated.set(message);
    }

    public remove(message: Message): Message | undefined {
        return this.messages().splice(this.messages().indexOf(message), 1)[0];
    }

    public clearAll(): void {
        this.messages.set([]);
    }

    public getByIndex(index: number): Message {
        return this.messages()[index];
    }

    public createMessage(isBot: boolean, text: string | undefined | null, buttons: MessageButton[] = [], image?: string): Message {
        let doHandover = false;
        const hiddenHandover: MessageButton | undefined = buttons.find((button: MessageButton): boolean => button.payload === '/hidden_handover');

        if (hiddenHandover) {
            doHandover = true;
        }

        return {
            text: this.cleanUpMessage(text),
            isBot,
            buttons,
            image,
            error: false,
            isPending: false,
            time: new Date().getTime(),
            doHandover,
        };
    }

    public async sendMessage(message: Message, addMessage = true): Promise<string> {
        message.isPending = true;

        if (addMessage) {
            this.add(message);
        }

        try {
            const response = await firstValueFrom<ItscMessageResponse[]>(this.apiService.conversation(message.text));

            return this.handleResponse(response);
        } catch {
            message.error = true;

            return this.handleResponse([
                {
                    event: 'bot',
                    timestamp: Date.now(),
                    // eslint-disable-next-line max-len
                    text: `Leider ist der Chatbot momentan nicht verfügbar. Bitte versuchen Sie es später noch einmal oder rufen Sie uns direkt an unter ${this.environmentService.servicePhoneNumber.title}.`,
                    data: {
                        // eslint-disable-next-line no-null/no-null
                        image: null,
                        buttons: [],
                    },
                },
            ]);
        } finally {
            message.isPending = false;
        }
    }

    private handleResponse(messages: ItscMessageResponse[]): string {
        let voiceMessage = '';

        messages.forEach((message: ItscMessageResponse): void => {
            const { text, data } = message;
            const buttons = data?.buttons ?? [];
            const image = data?.image ?? undefined;

            if (!text && buttons.length === 0 && !image) {
                return;
            }

            const newMessage: Message = this.createMessage(true, text, buttons, image);

            this.add(newMessage);

            voiceMessage += this.extractVoiceText(newMessage.text);
        });

        return voiceMessage;
    }

    /**
     * Convert markdown urls and line breaks.
     * @param message
     */
    private cleanUpMessage(message: string | undefined | null): string {
        // eslint-disable-next-line sonarjs/slow-regex
        return message?.replace(/\[(.*?)\]\((.*?)\)/gi, '<a href="$2">$1</a>').replace(/\n|\\n/g, '<br />') ?? '';
    }

    /**
     * Convert markdown urls to speak friendly text and linebreaks.
     * @param message
     */
    private extractVoiceText(message: string | undefined | null): string {
        // eslint-disable-next-line sonarjs/slow-regex
        return message?.replace(/(<([^>]+)>)/gi, '').replace(/\n|\\n/g, '.') ?? '';
    }

    private addTargetBlank(message: string): string {
        return message.replace(/href/g, 'target="_blank" href');
    }
}
